// Threat Profile Overlay Engine
//
// Tunes a baseline TIDA threat profile (from threatProfiles.json) using
// signals collected by Client Intelligence enrichment. Universal modules
// produce weight adjustments + evidence; the named-actor selector ranks
// specific groups whose targeting matches the client's profile and signals.
//
// Output:
//   {
//     profile: <baseline profile, unchanged>,
//     adjustments: <composed weight deltas across actors/vectors/assets>,
//     evidence: [string, ...] — human-readable reasons for the report
//     namedActors: [{ id, name, score, relevanceReasons, ... }, ...]
//     enrichmentAvailable: boolean
//   }

import { detectSubdomainSignals, subdomainSignalsToAdjustments } from './subdomainSignals'
import { detectRegulatorySignals } from './regulatorySignals'
import { detectDnsSecurityPosture } from './dnsSecurityPosture'
import { detectAttackSurfaceSignals } from './attackSurfaceSignals'
import { detectTechStackSignals } from './techStackSignals'
import { selectRelevantActors, summarizeTopActors } from './namedActorSelector'

export function applyClientIntelligenceOverlays({
  profile,
  enrichmentRow,
  businessScoping = {},
  technologyStack = []
}) {
  const result = {
    profile,
    adjustments: { threatActors: {}, attackVectors: {}, targetAssets: {} },
    evidence: [],
    namedActors: [],
    enrichmentAvailable: false
  }
  if (!profile) return result

  const enrichment = enrichmentRow?.enrichment || null
  const dns = enrichmentRow?.dns || null
  const shodan = enrichmentRow?.shodan || null
  const ctSubdomains = enrichmentRow?.ct_subdomains || []
  const profileKey = profile.verticalKey

  // 1. Subdomain operational surface
  const sub = detectSubdomainSignals(ctSubdomains, { profileKey })
  const subAdj = subdomainSignalsToAdjustments(sub.categories)
  composeAdjustments(result.adjustments, subAdj)

  // 2. Regulatory / org flags (works without enrichment if business scoping has them)
  const reg = detectRegulatorySignals(enrichment, businessScoping)
  composeAdjustments(result.adjustments, reg.adjustments)
  result.evidence.push(...reg.evidence)

  // 3. DNS-derived email security posture
  if (dns) {
    const dnsRes = detectDnsSecurityPosture(dns)
    composeAdjustments(result.adjustments, dnsRes.adjustments)
    result.evidence.push(...dnsRes.evidence)
    result.enrichmentAvailable = true
  }

  // 4. Attack surface (Shodan + subdomain volume)
  if (shodan || ctSubdomains.length > 0) {
    const surf = detectAttackSurfaceSignals({ shodan, ctSubdomains })
    composeAdjustments(result.adjustments, surf.adjustments)
    result.evidence.push(...surf.evidence)
    if (shodan) result.enrichmentAvailable = true
  }

  // 5. Tech stack vendor signals (also identifies named actors directly)
  const tech = detectTechStackSignals({ enrichment, technologyStack, profileKey })
  composeAdjustments(result.adjustments, tech.adjustments)
  result.evidence.push(...tech.evidence)
  if ((tech.vendorMatches || []).length > 0) result.enrichmentAvailable = true

  // 6. Named actor selection — rank and surface top actors
  const exposedServices = []
  if (Array.isArray(shodan?.services)) {
    shodan.services.forEach(s => {
      const banner = typeof s === 'string' ? s : (s?.product || s?.banner || s?.service || '')
      if (banner) exposedServices.push(banner)
    })
  }
  const ranked = selectRelevantActors({
    profileKey,
    subdomainCategories: sub.categories,
    vendorMatches: tech.vendorMatches || [],
    namedActorAlerts: tech.namedActorAlerts || [],
    exposedServices
  })
  result.namedActors = summarizeTopActors(ranked, 8)

  return result
}

function composeAdjustments(target, source) {
  for (const bucket of ['threatActors', 'attackVectors', 'targetAssets']) {
    const src = source?.[bucket] || {}
    for (const [k, v] of Object.entries(src)) {
      target[bucket][k] = (target[bucket][k] || 0) + v
    }
  }
}
